import React, { useEffect, useState } from "react";
import axios from "axios";
import { KeyRound, RotateCcw, CheckCircle2, AlertTriangle, Loader2, Clock } from "lucide-react";
import { CurveBottom } from "../components/Decor";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin/seed-demo`;

export const timeAgo = (iso) => {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.floor(h / 24); return `${d} day${d === 1 ? "" : "s"} ago`;
};

// "Today 3:10 PM" / "Yesterday 9:02 AM" / "Jun 12, 3:10 PM"
const fmtWhen = (iso) => {
  const d = new Date(iso), now = new Date();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return `Today ${time}`;
  if (sameDay(d, new Date(now.getTime() - 86400000))) return `Yesterday ${time}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
};

const errorText = (err) => {
  const s = err?.response?.status;
  if (s === 404) return "Demo reset is not enabled on this server (DEMO_SEED_KEY is not set).";
  if (s === 403) return "That seed key is wrong.";
  if (!err?.response) return "Could not reach the server. Check your connection.";
  return err.response?.data?.detail || "Reset failed.";
};

// Hidden reviewer tool (not linked anywhere): re-seeds the App Review demo family with the server's DEMO_SEED_KEY.
const AdminResetDemo = () => {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null); // {last_reset_at, count} | null

  useEffect(() => {
    setStatus(null);
    if (!key.trim()) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${BASE}/status`, { headers: { "X-Seed-Key": key } });
        setStatus(r.data); setError("");
      } catch (err) {
        if (err?.response?.status === 403 || err?.response?.status === 404) setError(errorText(err));
      }
    }, 600);
    return () => clearTimeout(t);
  }, [key]);

  const run = async () => {
    setConfirm(false); setBusy(true); setError(""); setResult(null);
    try {
      const r = await axios.post(BASE, null, { headers: { "X-Seed-Key": key } });
      setResult(r.data);
      setStatus((s) => { const entry = { at: new Date().toISOString(), source: "admin_page" }; return { last_reset_at: entry.at, count: (s?.count || 0) + 1, recent: [entry, ...(s?.recent || [])].slice(0, 5) }; });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-reset-page">
      <section className="relative bg-[#0c4a6e] text-white">
        <div className="max-w-3xl mx-auto px-6 pt-12 pb-24 text-center">
          <span className="inline-flex w-16 h-16 rounded-2xl bg-white/15 items-center justify-center"><RotateCcw size={32} /></span>
          <h1 className="mt-5 text-4xl sm:text-5xl font-bold" style={{ fontFamily: "Literata, serif" }}>Reset demo family</h1>
          <p className="mt-3 text-white/85 text-base md:text-lg max-w-xl mx-auto">Restores the App Review accounts, Maya and Leo, and their cheers to a clean state. Nothing else on the server is touched.</p>
        </div>
        <CurveBottom color="#f7fdfe" />
      </section>

      <div className="bg-[#f7fdfe] pb-20">
        <form onSubmit={(e) => { e.preventDefault(); if (key.trim()) setConfirm(true); }} className="max-w-md mx-auto px-6 -mt-10 relative z-10 bg-white rounded-3xl shadow-lg p-6 sm:p-8" data-testid="admin-reset-form">
          <label className="block text-sm font-bold text-[#0c4a6e]" htmlFor="seed-key">Seed key</label>
          <div className="mt-2 flex items-center gap-2 rounded-2xl border border-[#d3f2fa] bg-[#f7fdfe] px-3 focus-within:ring-2 focus-within:ring-[#1b8bc9]">
            <KeyRound size={16} className="text-[#1b8bc9] shrink-0" />
            <input id="seed-key" type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="DEMO_SEED_KEY" className="flex-1 bg-transparent py-3 outline-none text-sm" data-testid="admin-reset-key" />
          </div>
          {status && (
            <div className="mt-3" data-testid="admin-reset-status">
              <p className="text-xs font-semibold text-[#475569] inline-flex items-center gap-1">
                <Clock size={13} /> {status.last_reset_at ? `Last reset: ${timeAgo(status.last_reset_at)} (${status.count} total)` : "Never reset from this page yet"}
              </p>
              {status.recent?.length > 0 && (
                <ul className="mt-2 rounded-2xl bg-[#f7fdfe] border border-[#d3f2fa] divide-y divide-[#d3f2fa]" data-testid="admin-reset-history">
                  {status.recent.map((r, i) => (
                    <li key={r.at} className="px-3 py-1.5 text-xs text-[#0c4a6e] flex justify-between gap-2" data-testid={`admin-reset-history-${i}`}>
                      <span>{fmtWhen(r.at)}</span><span className="text-gray-500">{r.source === "admin_page" ? "admin page" : r.source}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <button type="submit" disabled={busy || !key.trim()} className="mt-4 w-full btn-pill bg-[#e5356f] hover:bg-[#c92a5e] disabled:opacity-50 text-white inline-flex items-center justify-center gap-2" data-testid="admin-reset-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />} {busy ? "Resetting..." : "Reset demo family"}
          </button>

          {error && (
            <div role="alert" className="mt-4 rounded-2xl bg-[#fff3d6] text-[#8a5a00] px-4 py-3 text-sm font-semibold flex items-start gap-2" data-testid="admin-reset-error">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}

          {result && (
            <div className="mt-4 rounded-2xl bg-[#e6f7ef] text-[#1f7a4d] px-4 py-3 text-sm" data-testid="admin-reset-result">
              <p className="font-bold flex items-center gap-2"><CheckCircle2 size={16} /> Demo family reset</p>
              <ul className="mt-2 space-y-1 text-[#0c4a6e]">
                <li><b>Parent:</b> {result.parent}</li>
                <li><b>Co-parent:</b> {result.coparent}</li>
                <li><b>Grandma:</b> {result.grandma}</li>
                <li><b>Password:</b> {result.password}</li>
                <li><b>Children:</b> {Object.keys(result.children || {}).join(", ")}</li>
              </ul>
            </div>
          )}
        </form>
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-3xl" data-testid="admin-reset-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "Literata, serif" }}>Reset the demo family?</AlertDialogTitle>
            <AlertDialogDescription>
              Maya and Leo's progress, the four cheers and the reviewer passwords go back to their starting state. Any changes a reviewer made will be lost. Real families are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="admin-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={run} className="rounded-full bg-[#e5356f] hover:bg-[#c92a5e]" data-testid="admin-reset-confirm-yes">Yes, reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminResetDemo;
